import React from 'react';

import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import SaveIcon from '@mui/icons-material/Save';
import SearchIcon from '@mui/icons-material/Search';
import TuneIcon from '@mui/icons-material/Tune';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import GuideInfoBox from '../components/GuideInfoBox';
import GuideMockContainer from '../components/GuideMockContainer';
import GuidePageLayout from '../components/GuidePageLayout';
import GuideSection from '../components/GuideSection';
import GuideStepList from '../components/GuideStepList';
import GuideTable from '../components/GuideTable';

const GuideSearchCondition: React.FC = () => {
  return (
    <GuidePageLayout
      title="検索条件の使い方"
      description="各管理画面の検索条件入力・保存・呼び出し・削除の共通操作を説明します。"
      breadcrumbs={[{ label: '共通機能', path: '/guide/common/overview' }]}
    >
      {/* 検索条件の入力 */}
      <GuideSection title="検索条件を入力する">
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          各管理画面の上部にある検索バーをクリックすると、条件入力ダイアログが開きます。条件を入力するとチップとして表示され、検索が実行されます。
        </Typography>
        <GuideStepList
          steps={[
            {
              label: '検索バーをクリックする',
              description:
                '検索バーをクリックすると保存済み条件のプルダウンが表示されます。条件詳細アイコン（チューニングアイコン）をクリックすると条件入力ダイアログが開きます。',
              content: (
                <GuideMockContainer label="検索バー">
                  <Stack direction="row" spacing={1} alignItems="center">
                    <IconButton size="small" sx={{ pointerEvents: 'none' }}>
                      <TuneIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
                    </IconButton>
                    <Paper
                      elevation={0}
                      sx={{
                        flex: 1,
                        border: 1,
                        borderColor: 'divider',
                        borderRadius: 2,
                        display: 'flex',
                        alignItems: 'center',
                        minHeight: 40,
                        px: 1.5,
                        py: 0.5,
                        gap: 1,
                        pointerEvents: 'none',
                      }}
                    >
                      <SearchIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                      <Typography variant="body2" color="text.disabled">
                        クリックして検索条件を入力...
                      </Typography>
                    </Paper>
                  </Stack>
                </GuideMockContainer>
              ),
            },
            {
              label: '条件入力ダイアログで条件を設定する',
              description:
                'テキスト入力、プルダウン選択、ユーザー選択、日付入力など、画面ごとに異なる入力タイプが表示されます。',
            },
            {
              label: '条件がチップとして検索バーに表示される',
              content: (
                <GuideMockContainer label="検索条件チップ表示例">
                  <Paper
                    elevation={0}
                    sx={{
                      border: 1,
                      borderColor: 'divider',
                      borderRadius: 2,
                      display: 'flex',
                      alignItems: 'center',
                      minHeight: 40,
                      px: 1.5,
                      py: 0.5,
                      gap: 0.5,
                      pointerEvents: 'none',
                    }}
                  >
                    <SearchIcon sx={{ color: 'text.secondary', fontSize: 20, flexShrink: 0 }} />
                    <Chip
                      size="small"
                      label="パートナー名: サンプル"
                      deleteIcon={<CloseIcon sx={{ fontSize: 14 }} />}
                      onDelete={() => {}}
                      sx={{ bgcolor: '#78C1F3', color: '#fff', fontWeight: 500 }}
                    />
                    <Chip
                      size="small"
                      label="パートナー種別: 法人"
                      deleteIcon={<CloseIcon sx={{ fontSize: 14 }} />}
                      onDelete={() => {}}
                      sx={{ bgcolor: '#B799FF', color: '#fff', fontWeight: 500 }}
                    />
                  </Paper>
                </GuideMockContainer>
              ),
            },
            {
              label: '条件を変更する場合はチップの「×」で削除し、再度検索バーから入力する',
            },
          ]}
        />
      </GuideSection>

      <GuideSection title="検索条件の入力タイプ">
        <GuideTable
          headers={['タイプ', '説明', '使用例']}
          rows={[
            ['テキスト入力', '自由入力。前方一致で検索', 'パートナー名、要員名等'],
            ['カタカナ入力', 'カタカナ変換付きテキスト入力', 'パートナー名（カナ）等'],
            ['プルダウン選択', '固定選択肢から1つ選択', 'ステータス、区分等'],
            ['ユーザー選択', 'ユーザー一覧から選択', '担当者、課長等'],
            ['パートナー選択', 'パートナー一覧から選択', '所属パートナー'],
            ['取引先選択', '取引先一覧から選択', '取引先'],
            ['プロジェクト選択', 'プロジェクト一覧から選択', 'プロジェクト'],
            ['要員選択', '要員一覧から選択', 'メンバー'],
            ['年度選択', '年度一覧から選択', '年度'],
            ['日時ピッカー（From/To）', '日時を期間指定で入力', '申請日、承認日等'],
            ['チェックボックス', 'ON/OFFで切替', '削除済みを含む、契約終了を含む等'],
          ]}
        />
      </GuideSection>

      {/* 検索条件の保存 */}
      <GuideSection title="検索条件を保存する（BUC-SRC01）">
        <GuideStepList
          steps={[
            { label: '管理画面で検索条件を入力する' },
            {
              label: '検索バーをクリックしてプルダウンを開く',
            },
            {
              label: 'プルダウン下部の「現在の検索条件を保存」ボタンを押下する',
              content: (
                <GuideMockContainer label="保存ボタン">
                  <Box sx={{ pointerEvents: 'none' }}>
                    <Divider />
                    <Box sx={{ px: 1.5, py: 1 }}>
                      <Button
                        variant="text"
                        size="small"
                        fullWidth
                        startIcon={<SaveIcon sx={{ fontSize: 16 }} />}
                        sx={{
                          borderRadius: 1,
                          textTransform: 'none',
                          fontWeight: 600,
                          fontSize: '0.75rem',
                          color: 'text.secondary',
                          justifyContent: 'flex-start',
                          px: 1,
                        }}
                      >
                        現在の検索条件を保存
                      </Button>
                    </Box>
                  </Box>
                </GuideMockContainer>
              ),
            },
          ]}
        />
        <Stack spacing={1} sx={{ mt: 1.5 }}>
          <GuideInfoBox>
            保存した検索条件は自分だけに表示されます（他ユーザーからは見えません）。
          </GuideInfoBox>
          <GuideInfoBox>
            同じ画面に複数の検索条件を保存できます。保存名は日時で自動付与されます。
          </GuideInfoBox>
        </Stack>
      </GuideSection>

      {/* 保存済み検索条件の呼び出し */}
      <GuideSection title="保存済み検索条件を呼び出す（BUC-SRC02）">
        <GuideStepList
          steps={[
            { label: '管理画面の検索バーをクリックする' },
            {
              label: 'プルダウンに保存済み条件が一覧表示される',
              content: (
                <GuideMockContainer label="保存済み条件プルダウン">
                  <Stack spacing={0} sx={{ pointerEvents: 'none' }}>
                    <Box
                      sx={{
                        px: 1.5,
                        py: 0.75,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        bgcolor: 'action.hover',
                        borderRadius: 1,
                      }}
                    >
                      <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
                        2025/07/01 10:30
                      </Typography>
                      <Stack direction="row" spacing={0.5}>
                        <Chip
                          size="small"
                          label="パートナー名: サンプル"
                          sx={{ bgcolor: '#78C1F3', color: '#fff', height: 20, fontSize: '0.7rem' }}
                        />
                        <Chip
                          size="small"
                          label="契約終了を含む"
                          sx={{ bgcolor: '#78909C', color: '#fff', height: 20, fontSize: '0.7rem' }}
                        />
                      </Stack>
                      <IconButton size="small" sx={{ p: 0.25 }}>
                        <DeleteOutlineIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                      </IconButton>
                    </Box>
                    <Box
                      sx={{
                        px: 1.5,
                        py: 0.75,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                      }}
                    >
                      <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
                        2025/06/15 14:00
                      </Typography>
                      <Stack direction="row" spacing={0.5}>
                        <Chip
                          size="small"
                          label="ステータス: 稼働中"
                          sx={{ bgcolor: '#B799FF', color: '#fff', height: 20, fontSize: '0.7rem' }}
                        />
                      </Stack>
                      <IconButton size="small" sx={{ p: 0.25 }}>
                        <DeleteOutlineIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                      </IconButton>
                    </Box>
                  </Stack>
                </GuideMockContainer>
              ),
            },
            { label: '使用したい条件を選択すると、検索バーに自動反映される' },
            { label: '必要に応じて条件を手動修正してから検索を実行する' },
          ]}
        />
        <GuideInfoBox>条件を呼び出した後に手動で修正してから検索することも可能です。</GuideInfoBox>
      </GuideSection>

      {/* 保存済み検索条件の削除 */}
      <GuideSection title="保存済み検索条件を削除する（BUC-SRC03）">
        <GuideStepList
          steps={[
            { label: '検索バーをクリックしてプルダウンを開く' },
            {
              label: '削除したい条件の右側にあるゴミ箱アイコンをクリックする',
              content: (
                <GuideMockContainer label="削除アイコン">
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={1}
                    sx={{ pointerEvents: 'none' }}
                  >
                    <Typography variant="caption" color="text.secondary">
                      2025/07/01 10:30
                    </Typography>
                    <Chip
                      size="small"
                      label="パートナー名: サンプル"
                      sx={{ bgcolor: '#78C1F3', color: '#fff', height: 20, fontSize: '0.7rem' }}
                    />
                    <IconButton size="small" sx={{ p: 0.25 }}>
                      <DeleteOutlineIcon sx={{ fontSize: 16, color: 'error.main' }} />
                    </IconButton>
                  </Stack>
                </GuideMockContainer>
              ),
            },
          ]}
        />
        <Stack spacing={1} sx={{ mt: 1.5 }}>
          <GuideInfoBox type="warning">削除は物理削除のため復元できません。</GuideInfoBox>
          <GuideInfoBox>他ユーザーの検索条件は削除できません。</GuideInfoBox>
        </Stack>
      </GuideSection>

      {/* 前提 */}
      <GuideSection title="前提">
        <GuideTable
          headers={['条件', '内容']}
          rows={[
            ['ログイン状態', 'ログイン済みであること（権限不問）'],
            [
              '対象画面',
              'ConditionListを使用している管理画面（パートナー、取引先、プロジェクト、要員、契約、資産、セキュリティチェック等）',
            ],
          ]}
        />
      </GuideSection>
    </GuidePageLayout>
  );
};

export default GuideSearchCondition;
